import React, { useState, useEffect } from 'react';
import { 
  LayoutGrid, 
  TrendingUp, 
  Sprout, 
  Beaker, 
  BarChart3, 
  Calendar, 
  AlertTriangle, 
  Eye, 
  Users,
  ChevronLeft,
  ChevronRight,
  Menu,
  Droplets,
  X,
  LineChart,
  ClipboardList,
  Package
} from 'lucide-react';
import { cn } from '@/lib/utils';

const MENU_ITEMS = {
  owner: [
    { id: 'overview', label: 'Farm Overview', icon: LayoutGrid },
    { id: 'room-tasks', label: 'Room Tasks', icon: ClipboardList },
    { id: 'forecast', label: 'Forecast', icon: LineChart },
    { id: 'production', label: 'Production', icon: TrendingUp },
    { id: 'actual-production', label: 'Actual Production', icon: Package },
    { id: 'rooms', label: 'Rooms', icon: Sprout },
    { id: 'watering', label: 'Watering', icon: Droplets },
    { id: 'composting', label: 'Composting', icon: Beaker },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'delays', label: 'Delays', icon: AlertTriangle },
    { id: 'logs', label: 'Logs', icon: Eye },
    { id: 'users', label: 'Users', icon: Users },
  ],
  grower: [
    { id: 'overview', label: 'Farm Overview', icon: LayoutGrid },
    { id: 'room-tasks', label: 'Room Tasks', icon: ClipboardList },
    { id: 'forecast', label: 'Forecast', icon: LineChart },
    { id: 'production', label: 'Production', icon: TrendingUp },
    { id: 'actual-production', label: 'Actual Production', icon: Package },
    { id: 'rooms', label: 'Rooms', icon: Sprout },
    { id: 'watering', label: 'Watering', icon: Droplets },
    { id: 'composting', label: 'Composting', icon: Beaker },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'delays', label: 'Delays', icon: AlertTriangle },
    { id: 'logs', label: 'Logs', icon: Eye },
  ],
  growing_supervisor: [
    { id: 'today', label: "Today's Work", icon: AlertTriangle },
    { id: 'forecast', label: 'Forecast', icon: LineChart },
    { id: 'production', label: 'Production', icon: TrendingUp },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
  ],
  composting_supervisor: [
    { id: 'today', label: "Today's Work", icon: Beaker },
    { id: 'forecast', label: 'Forecast', icon: LineChart },
    { id: 'active', label: 'Active Batches', icon: Sprout },
    { id: 'past', label: 'Past Batches', icon: Eye },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
  ],
};

const DashboardSidebar = ({ 
  role = 'owner', 
  activeTab, 
  onTabChange,
  className 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const menuItems = MENU_ITEMS[role] || MENU_ITEMS.owner;

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMouseEnter = () => {
    if (!isPinned && !isMobile) {
      setIsExpanded(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isPinned && !isMobile) {
      setIsExpanded(false);
    }
  };

  const handleItemClick = (itemId) => {
    onTabChange(itemId);
    if (!isPinned && !isMobile) {
      setIsExpanded(false);
    }
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const togglePin = () => {
    setIsPinned(!isPinned);
    setIsExpanded(!isPinned);
  };

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  // Mobile hamburger button (shown in header area)
  const MobileMenuButton = () => (
    <button
      onClick={toggleMobileMenu}
      className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-[#1A4D2E] text-white shadow-lg hover:bg-[#143D23] transition-colors"
      data-testid="mobile-menu-button"
    >
      {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
    </button>
  );

  // Mobile overlay backdrop
  const MobileBackdrop = () => (
    <div 
      className={cn(
        "md:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300",
        mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      )}
      onClick={() => setMobileOpen(false)}
      data-testid="mobile-backdrop"
    />
  );

  const sidebarClasses = cn(
    "fixed left-0 top-0 h-full z-40 transition-all duration-300 ease-in-out",
    "bg-[#1A4D2E] shadow-xl",
    // Desktop behavior
    !isMobile && (isExpanded ? "w-56" : "w-16"),
    // Mobile behavior
    isMobile && "w-64",
    isMobile && (mobileOpen ? "translate-x-0" : "-translate-x-full"),
    className
  );

  return (
    <>
      {/* Mobile menu button */}
      {isMobile && <MobileMenuButton />}
      
      {/* Mobile backdrop */}
      {isMobile && <MobileBackdrop />}

      {/* Sidebar */}
      <div
        className={sidebarClasses}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid="dashboard-sidebar"
      >
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-[#2d6b47]">
          {(isExpanded || isMobile) ? (
            <>
              <div className="flex items-center gap-2">
                <Sprout className="w-6 h-6 text-white" />
                <span className="font-bold text-white text-lg">A3ROS</span>
              </div>
              {!isMobile && (
                <button
                  onClick={togglePin}
                  className="p-1 rounded hover:bg-[#2d6b47] transition-colors"
                  data-testid="pin-sidebar-btn"
                >
                  {isPinned ? (
                    <ChevronLeft className="w-5 h-5 text-white/70" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-white/70" />
                  )}
                </button>
              )}
              {isMobile && (
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-1 rounded hover:bg-[#2d6b47] transition-colors"
                  data-testid="close-mobile-menu"
                >
                  <X className="w-5 h-5 text-white/70" />
                </button>
              )}
            </>
          ) : (
            <button
              onClick={() => setIsExpanded(true)}
              className="w-full flex justify-center"
              data-testid="expand-sidebar-btn"
            >
              <Menu className="w-6 h-6 text-white" />
            </button>
          )}
        </div>

        {/* Menu Items */}
        <nav className="py-4 px-2 space-y-1 overflow-y-auto max-h-[calc(100vh-4rem)]">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200",
                  "hover:bg-[#2d6b47]",
                  isActive 
                    ? "bg-white/20 text-white" 
                    : "text-white/70 hover:text-white"
                )}
                data-testid={`sidebar-${item.id}`}
              >
                <Icon className={cn(
                  "w-5 h-5 flex-shrink-0",
                  isActive ? "text-white" : "text-white/70"
                )} />
                {(isExpanded || isMobile) && (
                  <span className={cn(
                    "text-sm font-medium whitespace-nowrap",
                    isActive ? "text-white" : "text-white/70"
                  )}>
                    {item.label}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Active Indicator for collapsed desktop state */}
        {!isExpanded && !isMobile && (
          <div className="absolute left-0 top-0 h-full w-1 pointer-events-none">
            {menuItems.map((item, index) => {
              const isActive = activeTab === item.id;
              if (!isActive) return null;
              return (
                <div
                  key={item.id}
                  className="absolute w-1 h-8 bg-white rounded-r-full transition-all duration-200"
                  style={{ top: `${80 + index * 52}px` }}
                />
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default DashboardSidebar;
